import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { SITE } from '../seo/site';
import { getSeoForPath, absoluteUrl } from '../seo/routes';
import {
  buildOrganizationSchema,
  buildWebSiteSchema,
  buildBreadcrumbSchema,
  buildServiceSchema,
} from '../seo/schema';

const JSON_LD_ID = 'saad-jsonld';

function upsertMeta(attr, key, content) {
  if (content == null || content === '') return;
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function upsertJsonLd(graph) {
  let el = document.getElementById(JSON_LD_ID);
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = JSON_LD_ID;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify({
    '@context': 'https://schema.org',
    '@graph': graph,
  });
}

const SERVICE_PATHS = new Set([
  '/services/infogerance',
  '/services/depannage',
  '/services/infrastructure',
  '/services/m365',
  '/services/securite',
  '/services/conseil-dsi',
]);

/**
 * Met à jour title, meta, canonical, Open Graph et JSON-LD à chaque changement de route.
 */
export const SEO = () => {
  const { pathname } = useLocation();
  const seo = getSeoForPath(pathname);
  const canonical = absoluteUrl(pathname);
  const robots = seo.robots || (seo.noindex ? 'noindex, follow' : 'index, follow');

  useEffect(() => {
    document.documentElement.lang = SITE.language;

    document.title = seo.title;
    upsertMeta('name', 'description', seo.description);
    upsertMeta('name', 'keywords', seo.keywords);
    upsertMeta('name', 'robots', robots);
    upsertMeta('name', 'author', 'Richard SAAD — SAAD Informatique');
    upsertMeta('name', 'geo.region', 'FR-69');
    upsertMeta('name', 'geo.placename', 'Lyon');

    upsertLink('canonical', canonical);

    upsertMeta('property', 'og:type', 'website');
    upsertMeta('property', 'og:locale', SITE.locale);
    upsertMeta('property', 'og:site_name', SITE.brand);
    upsertMeta('property', 'og:title', seo.title);
    upsertMeta('property', 'og:description', seo.description);
    upsertMeta('property', 'og:url', canonical);
    upsertMeta('property', 'og:image', SITE.defaultImage);

    upsertMeta('name', 'twitter:card', 'summary');
    upsertMeta('name', 'twitter:title', seo.title);
    upsertMeta('name', 'twitter:description', seo.description);
    upsertMeta('name', 'twitter:image', SITE.defaultImage);

    const graph = [buildOrganizationSchema(), buildWebSiteSchema()];

    if (seo.breadcrumb?.length) {
      graph.push(buildBreadcrumbSchema(seo.breadcrumb));
    }

    if (SERVICE_PATHS.has(pathname)) {
      graph.push(buildServiceSchema(pathname, seo));
    }

    upsertJsonLd(graph);
  }, [pathname, seo, canonical, robots]);

  return null;
};
